'use client';

import type { ReactNode } from 'react';
import cs from 'classnames';
import { styled } from 'styled-components';
import TextLink from '@dvd/components/TextLink';

type IconCardColumnProps = {
  /**
   * IconCard children; should be a multiple of 3 or 4
   */
  children: ReactNode[];
  /**
   * Optional text for the cta
   */
  ctaText?: string;
  /**
   * Description paragraph for the module
   */
  description?: string;
  /**
   * Headline for the module
   */
  headline?: string;
  /**
   * Optional href for the cta
   */
  href?: string;
};

const IconCardColumnContainer = styled.div`
  padding-block: ${({ theme }) => theme.spacing[4]};
`;

const IconCardColumnHeading = styled.div`
  margin: 0 auto ${({ theme }) => theme.spacing[3]};
  text-align: center;
`;

const IconCardColumnCards = styled.div`
  display: grid;
  grid-gap: ${({ theme }) => theme.spacing[2]};
  justify-content: center;

  ${({ theme }) => theme.grid.columns.small}

  & > * {
    align-self: flex-start;
    grid-column: span 2;
  }

  ${({ theme }) => theme.mq.minWidth.medium} {
    ${({ theme }) => theme.grid.columns.medium}
    & > * {
      grid-column: span 3;
    }
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    ${({ theme }) => theme.grid.columns.large}
    &.threeUp > * {
      grid-column: span 4;
    }

    &.fourUp > * {
      grid-column: span 3;
    }
  }
`;

const IconCardColumns = ({
  children,
  ctaText = 'Learn more',
  description,
  headline,
  href,
}: IconCardColumnProps) => {
  const threeUp = children.length % 3 === 0;
  const fourUp = children.length % 4 === 0;
  return (
    <IconCardColumnContainer>
      {headline || description ? (
        <IconCardColumnHeading>
          {headline ? <h2>{headline}</h2> : null}
          {description ? <p>{description}</p> : null}
          {href ? <TextLink cta={{ href, text: ctaText }} /> : null}
        </IconCardColumnHeading>
      ) : null}
      <IconCardColumnCards className={cs({ threeUp, fourUp })}>
        {children}
      </IconCardColumnCards>
    </IconCardColumnContainer>
  );
};

export default IconCardColumns;
